import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TokenService } from '../../shared/service/token.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { PERMISSIONS } from 'src/app/constants/permissions.constants';
import { map, Observable, of, switchMap, take, tap } from 'rxjs';

export const authGuard = (route: any): Observable<boolean> => {
  const tokenService = inject(TokenService);
  const permissionService = inject(PermissionsService);
  const router = inject(Router);

  if (!tokenService.getToken()) {
    const currentUrl = router.url;
    tokenService.setRedirectUrl(currentUrl);
    router.navigate(['/']);
    return of(false);
  }

  const path = route.routeConfig?.path ?? '';
  const requiredPermissions = getRequiredPermissions(path);

  if (!requiredPermissions.length) {
    return of(true); // No specific permissions required
  }

  const baseLocationPaths = [
    'planning-sheet',
    'planning-report',
    'staff',
    'organization',
    'streams',
    'chat'
  ];

  const useBasePermissions = baseLocationPaths.includes(path);

  // Load relevant permissions
  const load$ = useBasePermissions
    ? permissionService.loadUserRoleAndPermissions()
    : permissionService.getCurrentLocPermissions(); // memoized

  const permissions$ = useBasePermissions
    ? permissionService.userPermissions$
    : permissionService.currentPermissions$;

  return load$.pipe(
    switchMap(() =>
      permissions$.pipe(
        take(1),
        map((permissions) => requiredPermissions.some((perm) => permissions.includes(perm))),
        tap((authorized) => {
          if (!authorized) {
            router.navigate(['/no-access'], {
              state: { reason: 'no-access' }
            });
          }
        })
      )
    )
  );
};


// Example function to map routes to required permissions
function getRequiredPermissions(path: string): string[] {
  const permissionsMap: { [key: string]: string[] } = {    
    'planning-sheet':[  
      PERMISSIONS.PLANNING_SHEET_READ,
      PERMISSIONS.PLANNING_SHEET_WRITE,],
    'planning-report':[  
      PERMISSIONS.PLANNING_SHEET_READ,
      PERMISSIONS.PLANNING_SHEET_WRITE,],
    'duties':[PERMISSIONS.DUTY_PLANNING_ACTIVATION_READ,
      PERMISSIONS.DUTY_PLANNING_ACTIVATION_WRITE,
      PERMISSIONS.DUTY_MANAGEMENT_READ,
      PERMISSIONS.DUTY_MANAGEMENT_WRITE,
      PERMISSIONS.DUTY_DEPARTMENT_MAPPING_READ,
      PERMISSIONS.DUTY_DEPARTMENT_MAPPING_WRITE],
    'duties/:id':[PERMISSIONS.DUTY_PLANNING_ACTIVATION_READ,
      PERMISSIONS.DUTY_PLANNING_ACTIVATION_WRITE,
      PERMISSIONS.DUTY_MANAGEMENT_READ,
      PERMISSIONS.DUTY_MANAGEMENT_WRITE,
      PERMISSIONS.DUTY_DEPARTMENT_MAPPING_READ,
      PERMISSIONS.DUTY_DEPARTMENT_MAPPING_WRITE],
    'staff': [PERMISSIONS.GLOBAL_STAFF_BASIC_INFORMATION_WRITE, PERMISSIONS.GLOBAL_STAFF_BASIC_INFORMATION_READ,PERMISSIONS.GLOBAL_STAFF_CONFIGURATION_DETAILS_WRITE,PERMISSIONS.GLOBAL_STAFF_CONFIGURATION_DETAILS_READ,PERMISSIONS.GLOBAL_STAFF_EMPLOYMENT_DETAILS_WRITE,PERMISSIONS.GLOBAL_STAFF_EMPLOYMENT_DETAILS_READ],
    'staff/:id': [PERMISSIONS.STAFF_BASIC_INFORMATION_WRITE, PERMISSIONS.STAFF_BASIC_INFORMATION_READ,PERMISSIONS.STAFF_CONFIGURATION_DETAILS_WRITE,PERMISSIONS.STAFF_CONFIGURATION_DETAILS_READ,PERMISSIONS.STAFF_EMPLOYMENT_DETAILS_WRITE,PERMISSIONS.STAFF_EMPLOYMENT_DETAILS_READ],
    'activities': [PERMISSIONS.ACTIVITY_DETAILS_WRITE, PERMISSIONS.ACTIVITY_DETAILS_READ],
    'activities/:id': [PERMISSIONS.ACTIVITY_DETAILS_WRITE, PERMISSIONS.ACTIVITY_DETAILS_READ],    
    'staff-log-dashboard': [PERMISSIONS.GLOBAL_STAFF_BASIC_INFORMATION_WRITE, PERMISSIONS.GLOBAL_STAFF_BASIC_INFORMATION_READ,PERMISSIONS.GLOBAL_STAFF_CONFIGURATION_DETAILS_WRITE,PERMISSIONS.GLOBAL_STAFF_CONFIGURATION_DETAILS_READ,PERMISSIONS.GLOBAL_STAFF_EMPLOYMENT_DETAILS_WRITE,PERMISSIONS.GLOBAL_STAFF_EMPLOYMENT_DETAILS_READ],
    'staffs': [PERMISSIONS.GLOBAL_STAFF_BASIC_INFORMATION_WRITE, PERMISSIONS.GLOBAL_STAFF_BASIC_INFORMATION_READ,PERMISSIONS.GLOBAL_STAFF_CONFIGURATION_DETAILS_WRITE,PERMISSIONS.GLOBAL_STAFF_CONFIGURATION_DETAILS_READ,PERMISSIONS.GLOBAL_STAFF_EMPLOYMENT_DETAILS_WRITE,PERMISSIONS.GLOBAL_STAFF_EMPLOYMENT_DETAILS_READ],
    'locations': [PERMISSIONS.LOCATION_DETAILS_WRITE, PERMISSIONS.LOCATION_DETAILS_READ],
    'locations/:id': [PERMISSIONS.LOCATION_DETAILS_WRITE, PERMISSIONS.LOCATION_DETAILS_READ],
    'organization': [PERMISSIONS.ORGANISATION_DETAILS_READ, PERMISSIONS.ORGANISATION_DETAILS_WRITE],
    'holidays': [PERMISSIONS.HOLIDAY_DETAILS_READ, PERMISSIONS.HOLIDAY_DETAILS_WRITE, PERMISSIONS.HOLIDAY_IMPORTED_DATA_READ, PERMISSIONS.HOLIDAY_IMPORTED_DATA_WRITE],
    'holidays/:id': [PERMISSIONS.HOLIDAY_DETAILS_READ, PERMISSIONS.HOLIDAY_DETAILS_WRITE, PERMISSIONS.HOLIDAY_IMPORTED_DATA_READ, PERMISSIONS.HOLIDAY_IMPORTED_DATA_WRITE],
    'events': [PERMISSIONS.EVENT_DETAILS_WRITE, PERMISSIONS.EVENT_DETAILS_READ],
    'events/:id': [PERMISSIONS.EVENT_DETAILS_WRITE, PERMISSIONS.EVENT_DETAILS_READ],    
    'configuration': [//this path is not used currently.since only the dropdown is showing instead of navigation
      PERMISSIONS.LOCATION_DETAILS_WRITE, PERMISSIONS.LOCATION_DETAILS_READ,
      PERMISSIONS.ORGANISATION_DETAILS_READ, PERMISSIONS.ORGANISATION_DETAILS_WRITE,
      PERMISSIONS.EVENT_DETAILS_WRITE, PERMISSIONS.EVENT_DETAILS_READ,
      PERMISSIONS.ACTIVITY_DETAILS_WRITE, PERMISSIONS.ACTIVITY_DETAILS_READ,
      PERMISSIONS.HOLIDAY_DETAILS_READ, PERMISSIONS.HOLIDAY_DETAILS_WRITE, PERMISSIONS.HOLIDAY_IMPORTED_DATA_READ, PERMISSIONS.HOLIDAY_IMPORTED_DATA_WRITE,
      PERMISSIONS.STAFF_BASIC_INFORMATION_WRITE, PERMISSIONS.STAFF_BASIC_INFORMATION_READ,PERMISSIONS.STAFF_CONFIGURATION_DETAILS_WRITE,PERMISSIONS.STAFF_CONFIGURATION_DETAILS_READ,PERMISSIONS.STAFF_EMPLOYMENT_DETAILS_WRITE,PERMISSIONS.STAFF_EMPLOYMENT_DETAILS_READ,
      PERMISSIONS.DUTY_PLANNING_ACTIVATION_READ,
      PERMISSIONS.DUTY_PLANNING_ACTIVATION_WRITE,
      PERMISSIONS.DUTY_MANAGEMENT_READ,
      PERMISSIONS.DUTY_MANAGEMENT_WRITE,
      PERMISSIONS.DUTY_DEPARTMENT_MAPPING_READ,
      PERMISSIONS.DUTY_DEPARTMENT_MAPPING_WRITE
    ],
    'streams': [PERMISSIONS.STREAM_MANAGEMENT_READ, PERMISSIONS.STREAM_MANAGEMENT_WRITE],
    'chat': [
      PERMISSIONS.CHAT_CONFIGURATION_READ,
      PERMISSIONS.CHAT_CONFIGURATION_WRITE,
    ],
  };
  return permissionsMap[path] || [];
}